"use client";

import React from 'react';
import Link from 'next/link';
import Breadcrumb from '../ui/Breadcrumb';
import { useTranslations } from 'next-intl';
import { ContactIcon } from '../icons';
import styles from './Contact.module.css';

interface ContactContentProps {
  translations: {
    hero: { title: string };
    beforeContact: {
      title: string;
      description: string;
      items: {
        email: string;
        orderNumber: string;
        giftCardDetails: string;
        description: string;
        screenshots: string;
      };
      note: string;
    };
    commonQuestions: {
      title: string;
      description: string;
      links: {
        faq: { title: string; description: string };
        refund: { title: string; description: string };
        crypto: { title: string; description: string };
        redeem: { title: string; description: string };
      };
    };
    giftCardHelp: {
      title: string;
      description: string;
      intro: string;
      steps: {
        checkEmail: string;
        verifyPayment: string;
        reviewInstructions: string;
        checkCountry: string;
      };
      note: string;
    };
    orderPayment: {
      title: string;
      description: string;
      items: {
        confirmations: string;
        delays: string;
        network: string;
        notReceived: string;
        redemption: string;
        doublePayment: string;
      };
      note: string;
    };
    business: {
      title: string;
      description: string;
      email: string;
      note: string;
      response: string;
    };
    security: {
      title: string;
      description: string;
      warning: string;
      items: {
        privateKeys: string;
        seedPhrases: string;
        credentials: string;
      };
      alert: string;
    };
    commitment: {
      title: string;
      description: string;
      items: {
        support: string;
        transparency: string;
        payments: string;
        delivery: string;
        help: string;
      };
      note: string;
    };
  };
  children?: React.ReactNode;
}

const IconSvg = ({ path, color = 'white' }: { path: string; color?: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ color }}><path d={path} /></svg>
);

const CheckItem = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.checkItem}>
    <svg className={styles.checkItemIcon} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
    <p className={styles.checkItemText}>{children}</p>
  </div>
);

const WarningItem = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.warningItem}>
    <span className={styles.warningItemDot} />
    <p className={styles.warningItemText}>{children}</p>
  </div>
);

const InfoCard = ({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) => (
  <div className={styles.infoCard}>
    <div className={styles.infoCardIconWrap}>{icon}</div>
    <h3 className={styles.infoCardTitle}>{title}</h3>
    {children}
  </div>
);

const LinkCard = ({ icon, title, description, href }: { icon: React.ReactNode; title: string; description: string; href: string }) => (
  <Link href={href} className={styles.linkCard}>
    <div className={styles.linkCardInner}>
      <div className={styles.linkCardIconWrap}>{icon}</div>
      <div>
        <p className={styles.linkCardTitle}>{title}</p>
        <p className={styles.linkCardDescription}>{description}</p>
      </div>
    </div>
  </Link>
);

const ContactContent: React.FC<ContactContentProps> = ({ translations: t, children }) => {
  const tCommon = useTranslations('common');
  
  const breadcrumbItems = [
    { label: tCommon('breadcrumb.home'), path: '/' },
    { label: tCommon('breadcrumb.contact'), path: '/contact' },
  ];

  return (
    <div className={styles.contactPage}>
      <div className={styles.contactBreadcrumbBar}>
        <div className={styles.contactContainer}><Breadcrumb items={breadcrumbItems} /></div>
      </div>

      <div className={styles.contactHero}>
        <div className={styles.contactContainer}>
          <div className={styles.contactHeroInner}>
            <h1 className={styles.contactHeroTitle}>{t.hero.title}</h1>
          </div>
        </div>
      </div>

      <div className={styles.contactContainer}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t.beforeContact.title}</h2>
          <p className={styles.sectionDescription}>{t.beforeContact.description}</p>
          <div className={styles.paper}>
            <CheckItem>{t.beforeContact.items.email}</CheckItem>
            <CheckItem>{t.beforeContact.items.orderNumber}</CheckItem>
            <CheckItem>{t.beforeContact.items.giftCardDetails}</CheckItem>
            <CheckItem>{t.beforeContact.items.description}</CheckItem>
            <CheckItem>{t.beforeContact.items.screenshots}</CheckItem>
            <p className={styles.sectionNote}>{t.beforeContact.note}</p>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t.commonQuestions.title}</h2>
          <p className={styles.sectionDescription}>{t.commonQuestions.description}</p>
          <div className={styles.gridTwo}>
            <LinkCard
              icon={<IconSvg path="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" color="var(--color-primary)" />}
              title={t.commonQuestions.links.faq.title}
              description={t.commonQuestions.links.faq.description}
              href="/faq"
            />
            <LinkCard
              icon={<IconSvg path="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" color="var(--color-primary)" />}
              title={t.commonQuestions.links.refund.title}
              description={t.commonQuestions.links.refund.description}
              href="/return"
            />
            <LinkCard
              icon={<IconSvg path="M17.06 11.57c.59-.69.94-1.59.94-2.57 0-1.86-1.27-3.43-3-3.87V3h-2v2h-2V3H9v2H6v2h2v10H6v2h3v2h2v-2h2v2h2v-2c2.21 0 4-1.79 4-4 0-1.45-.78-2.73-1.94-3.43zM10 7h4c1.1 0 2 .9 2 2s-.9 2-2 2h-4V7zm5 10h-5v-4h5c1.1 0 2 .9 2 2s-.9 2-2 2z" color="var(--color-primary)" />}
              title={t.commonQuestions.links.crypto.title}
              description={t.commonQuestions.links.crypto.description}
              href="/faq#crypto"
            />
            <LinkCard
              icon={<IconSvg path="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z" color="var(--color-primary)" />}
              title={t.commonQuestions.links.redeem.title}
              description={t.commonQuestions.links.redeem.description}
              href="/faq#redeem"
            />
          </div>
        </div>

        <div className={styles.gridTwoLarge}>
          <InfoCard
            icon={<IconSvg path="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z" />}
            title={t.giftCardHelp.title}
          >
            <p className={styles.infoCardText}>{t.giftCardHelp.description}</p>
            <p className={`${styles.infoCardText} ${styles.infoCardTextBold}`}>{t.giftCardHelp.intro}</p>
            <div className={styles.paperCheckList}>
              <CheckItem>{t.giftCardHelp.steps.checkEmail}</CheckItem>
              <CheckItem>{t.giftCardHelp.steps.verifyPayment}</CheckItem>
              <CheckItem>{t.giftCardHelp.steps.reviewInstructions}</CheckItem>
              <CheckItem>{t.giftCardHelp.steps.checkCountry}</CheckItem>
            </div>
            <p className={styles.sectionNote}>{t.giftCardHelp.note}</p>
          </InfoCard>
          <InfoCard
            icon={<IconSvg path="M19 14V6c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zm-2 0H3V6h14v8zm-7-7c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm13 0v11c0 1.1-.9 2-2 2H4v-2h17V7h2z" />}
            title={t.orderPayment.title}
          >
            <p className={styles.infoCardText}>{t.orderPayment.description}</p>
            <div className={styles.paperCheckList}>
              <CheckItem>{t.orderPayment.items.confirmations}</CheckItem>
              <CheckItem>{t.orderPayment.items.delays}</CheckItem>
              <CheckItem>{t.orderPayment.items.network}</CheckItem>
              <CheckItem>{t.orderPayment.items.notReceived}</CheckItem>
              <CheckItem>{t.orderPayment.items.redemption}</CheckItem>
              <CheckItem>{t.orderPayment.items.doublePayment}</CheckItem>
            </div>
            <p className={styles.sectionNote}>{t.orderPayment.note}</p>
          </InfoCard>
        </div>

        <div className={styles.gridThree}>
          <InfoCard
            icon={<IconSvg path="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm-4-9.15L6.47 11.33c-.38-.38-.88-.58-1.41-.58H4v4.12l1.21.81c.39.26.62.71.62 1.18V20h6v-3.27c0-.59-.26-1.15-.71-1.53L9 13.6V10h-.01l.01-.02v-.98c0-1.93 1.57-3.5 3.5-3.5s3.5 1.57 3.5 3.5v.98l.01.02H16v3.6l-2.12 1.6c-.45.38-.71.94-.71 1.53V20h6v-3.14c0-.47.23-.92.62-1.18L21 14.87V10.5h-1.06c-.53 0-1.03.21-1.41.58L17 12.85V9c0-2.76-2.24-5-5-5s-5 2.24-5 5v3.85z" />}
            title={t.business.title}
          >
            <p className={styles.infoCardText}>{t.business.description}</p>
            <p className={styles.infoCardHighlight}>{t.business.email}</p>
            <p className={styles.infoCardSmallText}>{t.business.note}</p>
            <p className={`${styles.infoCardSmallText} ${styles.infoCardSmallTextSpaced}`}>{t.business.response}</p>
          </InfoCard>
          <InfoCard
            icon={<IconSvg path="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />}
            title={t.security.title}
          >
            <p className={styles.infoCardText}>{t.security.description}</p>
            <p className={styles.warningText}>{t.security.warning}</p>
            <WarningItem>{t.security.items.privateKeys}</WarningItem>
            <WarningItem>{t.security.items.seedPhrases}</WarningItem>
            <WarningItem>{t.security.items.credentials}</WarningItem>
            <div className={styles.warningAlertBox}>
              <svg className={styles.warningAlertIcon} viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
              </svg>
              <p className={styles.warningAlertText}>{t.security.alert}</p>
            </div>
          </InfoCard>
          <InfoCard
            icon={<IconSvg path="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />}
            title={t.commitment.title}
          >
            <p className={styles.infoCardText}>{t.commitment.description}</p>
            <CheckItem>{t.commitment.items.support}</CheckItem>
            <CheckItem>{t.commitment.items.transparency}</CheckItem>
            <CheckItem>{t.commitment.items.payments}</CheckItem>
            <CheckItem>{t.commitment.items.delivery}</CheckItem>
            <CheckItem>{t.commitment.items.help}</CheckItem>
            <p className={styles.infoCardPrimaryNote}>{t.commitment.note}</p>
          </InfoCard>
        </div>

        {children}
      </div>
    </div>
  );
};

export default ContactContent;
